import { CloseIcon, SearchIcon } from '@chakra-ui/icons';
import { Box, Flex, Input, InputGroup, InputLeftElement, InputRightElement, List, ListItem, Spinner, Text, useColorModeValue } from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { useGetTargetDevicesQuery } from 'store/facebookServices/facebookServicesApi';

const shouldRemoveDevice = (newDevice, existingAudience) => {

    return (
        newDevice.name === existingAudience.name
    );
};

const filterSelectedDevices = (newDevice, selectedUserDevices) => {
    //console.log({ newDevice, selectedUserDevices });
    return selectedUserDevices.filter((device) => !shouldRemoveDevice(newDevice, device));
};

const ExcludeDevice = ({ adsAccountId, app_store, onIncludeDeviceChange, preFilledTargets, userDevice }) => {

    const inputBackground = useColorModeValue("#f8f6fb", "#1e1530");
    const borderColor = useColorModeValue('gray.300', 'gray.500');
    const hoverColor = useColorModeValue('#f0f0f0', 'gray.700');

    const inputRef = useRef(null);
    const popupRef = useRef(null);

    const [searchTerm, setSearchTerm] = useState('');
    const [userDeviceOptions, setUserDeviceOptions] = useState([]);
    const [selectedUserDevices, setselectedUserDevices] = useState([])

    const [showPopup, setShowPopup] = useState(false);
    const [isInputFocused, setIsInputFocused] = useState(false);

    const { data: userDevices, isLoading, isFetching, refetch } = useGetTargetDevicesQuery({ adsAccountId}, { skip: !adsAccountId });

    useEffect(() => {
        if (userDevices?.data && preFilledTargets?.length > 0) {
            const preSelectedDevices = userDevices.data
                .filter(device => preFilledTargets.includes(device.name))
                .filter(device => !userDevice.includes(device.name));
    
            const isSame =
                preSelectedDevices.length === selectedUserDevices.length &&
                preSelectedDevices.every(preDevice =>
                    selectedUserDevices.some(selDevice => selDevice.name === preDevice.name)
                );
    
            if (!isSame) {
                setselectedUserDevices(preSelectedDevices);
                onIncludeDeviceChange(preSelectedDevices);
            }

        }
    }, [userDevices, preFilledTargets, userDevice]);

    useEffect(() => {
        if (!userDevices?.data) return;
    
        // Determine which platform is allowed
        const isIOS = app_store === 'itunes';
        const isAndroid = app_store === 'google_play';
    
        // Filter selected devices based on current app store
        const filteredSelectedDevices = selectedUserDevices.filter(device => {
            if (isAndroid) return device.platform === 'Android';
            if (isIOS) return device.platform === 'iOS';
            return true; // if "All", don't filter
        });
    
        // If any devices were removed (i.e., filtered), update state and notify parent
        if (filteredSelectedDevices.length !== selectedUserDevices.length) {
            setselectedUserDevices(filteredSelectedDevices);
            onIncludeDeviceChange(filteredSelectedDevices);
        }
    }, [app_store, selectedUserDevices, userDevices]);
    

    useEffect(() => {
        if (searchTerm?.length > 0) {
            const filtered = userDevices?.data
                ?.filter((device) =>
                    device.description.toLowerCase().includes(searchTerm.toLowerCase())
                )
                ?.filter((device) => {
                    const isExcluded = userDevice.includes(device.name);
                    const isAndroid = device.platform === 'Android';
                    const isIOS = device.platform === 'iOS';
    
                    if (app_store === 'google_play') return isAndroid && !isExcluded;
                    if (app_store === 'itunes') return isIOS && !isExcluded;
                    return !isExcluded; // Default "All"
                });
    
            setUserDeviceOptions(filtered || []);
            setShowPopup(filtered?.length > 0);
        } else {
            setUserDeviceOptions([]);
            setShowPopup(false);
        }
    }, [searchTerm, userDevices, userDevice, app_store]);        

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                inputRef.current &&
                !inputRef.current.contains(event.target) &&
                popupRef.current &&
                !popupRef.current.contains(event.target)
            ) {
                setShowPopup(false);
            }

        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleDeviceSelect = (device) => {

        setselectedUserDevices(prevDevices => {
            const filteredDevices = filterSelectedDevices(device, prevDevices);
            const updatedDevices = [...filteredDevices, device]
            setTimeout(() => onIncludeDeviceChange(updatedDevices), 0);
            return updatedDevices;
        });

        setSearchTerm('');
        setShowPopup(false);
        setUserDeviceOptions([]);
    };

    const removeDeviceEntry = (name) => {
        setselectedUserDevices(prevDevices => {
            const updatedDevices = prevDevices.filter(device => device.name !== name);
            setTimeout(() => onIncludeDeviceChange(updatedDevices), 0);
            return updatedDevices;
        });
    };


    return (
        <Box w={'100%'}>
            <Box mt={'5px'}>
                {selectedUserDevices?.map((device, index) => (
                    <Flex key={`${device?.name}+${index}`} bg={inputBackground} align={'center'} justify={'space-between'} width={'full'} py={'5px'} px={'10px'} borderRadius={'md'} mb={'5px'}>

                        <Flex flex={1} pr={'10px'} align={'center'}>
                            <Flex justify={'space-between'} flex={1} align={'center'}>
                                <Text ml={'5px'} fontSize={'14px'} fontWeight={'bold'}>{device?.description} </Text>
                            </Flex>
                        </Flex>
                        <CloseIcon _hover={{ cursor: 'pointer', color: 'red.400' }} boxSize={3} color='gray.500' onClick={() => removeDeviceEntry(device?.name)} />
                    </Flex>
                ))}
            </Box>
            <Box position="relative">
                <Flex>
                    <InputGroup >
                        <InputLeftElement height={'32px'}>
                            <SearchIcon boxSize={4} color='gray.500' />
                        </InputLeftElement>
                        <Input
                            ref={inputRef}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder={
                                app_store === 'itunes'
                                  ? 'Enter a device name (e.g. iPhone 5s)...'
                                  : 'Enter a device name (e.g. Samsung Galaxy S4)...'
                            }                              
                            autoComplete="off"
                            borderRadius={'md'}
                            size={'sm'}
                            onFocus={() => {
                                setIsInputFocused(true);
                                if (userDeviceOptions?.length > 0) {
                                    setShowPopup(true)
                                }
                            }}
                            onBlur={() => {
                                setTimeout(() => {
                                    setIsInputFocused(false);
                                    setShowPopup(false);
                                }, 150); // Allow time for click event
                            }}
                        />
                        <InputRightElement height={'32px'}>
                            {isFetching ? (
                                <Spinner boxSize={3} />
                            ) : (<>
                                {searchTerm?.length > 0 ? (
                                    <CloseIcon boxSize={3} color='gray.500' onClick={() => { setSearchTerm(''); }} />
                                ) : null}
                            </>)}
                        </InputRightElement>
                    </InputGroup>
                </Flex>

                <Flex pt={2}>
                    {showPopup && userDeviceOptions.length > 0 && (
                        <List
                            ref={popupRef}
                            position="absolute"
                            top="100%"
                            width="100%"
                            border="1px solid"
                            borderColor={borderColor}
                            backgroundColor={inputBackground}
                            zIndex="1000"
                            maxHeight="200px"
                            overflowY="auto"
                        >
                            {userDeviceOptions.map((device, index) => (
                                <ListItem
                                    key={index * Math.random() + device.name}
                                    p={'5px'}
                                    px={'10px'}
                                    cursor="pointer"
                                    _hover={{ backgroundColor: hoverColor }}
                                    onClick={() => handleDeviceSelect(device)}
                                >
                                    <Flex justify={'space-between'} >
                                        <Text fontSize={'14px'}>
                                            {device.description}
                                        </Text>
                                    </Flex>
                                </ListItem>
                            ))}
                        </List>
                    )}
                </Flex>
            </Box>

        </Box>
    );
};

export default ExcludeDevice;
